/**
 * Estructura de datos para la tabla de historial de compras
 * Define las columnas y el formato de los datos
 * 
 * Nota: Las funciones Cell reciben props de react-table que no pueden
 * ser validadas con PropTypes en este contexto. Se deshabilita la regla
 * para este archivo.
 */

/* eslint-disable react/prop-types */

import React from 'react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import Icon from '@mui/material/Icon';
import { Tooltip } from '@mui/material';
import PurchaseStatusBadge from '../components/PurchaseStatusBadge';
import { formatCurrency, formatDateShort, getTypeConfig } from 'services/purchaseService';

/**
 * Genera las columnas de la tabla
 */
export const getPurchaseTableColumns = () => [
  {
    Header: 'Fecha',
    accessor: 'date',
    width: '12%',
    Cell: ({ value }) => (
      <MDTypography variant="caption" fontWeight="medium" color="text">
        {formatDateShort(value)}
      </MDTypography>
    ),
  },
  {
    Header: 'N° Orden',
    accessor: 'orderNumber',
    width: '15%',
    Cell: ({ value }) => (
      <MDTypography variant="button" fontWeight="medium">
        {value}
      </MDTypography>
    ),
  },
  {
    Header: 'Plan',
    accessor: 'packageName',
    width: '15%',
    Cell: ({ value }) => (
      <MDBox display="flex" alignItems="center" gap={1}>
        <Icon fontSize="small" color="action">
          workspace_premium
        </Icon>
        <MDTypography variant="caption" fontWeight="medium">
          {value || 'N/A'}
        </MDTypography>
      </MDBox>
    ),
  },
  {
    Header: 'Licencias',
    accessor: 'licenses',
    width: '10%',
    align: 'center',
    Cell: ({ value }) => (
      <MDTypography variant="button" fontWeight="medium" color="info">
        {value || 0}
      </MDTypography>
    ),
  },
  {
    Header: 'Monto',
    accessor: 'total_amount',
    width: '15%',
    Cell: ({ value, row }) => (
      <MDTypography variant="button" fontWeight="medium" color="dark">
        {formatCurrency(value || 0, row.original.currency || 'COP')}
      </MDTypography>
    ),
  },
  {
    Header: 'Estado',
    accessor: 'status',
    width: '15%',
    Cell: ({ value }) => <PurchaseStatusBadge status={value} size="small" />,
  },
  {
    Header: 'Acciones',
    accessor: 'actions',
    width: '15%',
    align: 'center',
    Cell: ({ row }) => (
      <MDBox display="flex" gap={1} justifyContent="center">
        <Tooltip title="Ver detalles">
          <MDButton
            variant="text"
            color="info"
            size="small"
            iconOnly
            onClick={() => row.original.onViewDetails(row.original.id)}
          >
            <Icon>visibility</Icon>
          </MDButton>
        </Tooltip>
        {row.original.invoice && (
          <Tooltip title="Descargar factura">
            <MDButton
              variant="text"
              color="success"
              size="small"
              iconOnly
              onClick={() => row.original.onDownloadInvoice(row.original.invoice.number)}
            >
              <Icon>download</Icon>
            </MDButton>
          </Tooltip>
        )}
      </MDBox>
    ),
  },
];

/**
 * Transforma los datos de compras para la tabla
 * @param {Array} purchases - Array de compras del backend
 * @param {Function} onViewDetails - Handler para ver detalles
 * @param {Function} onDownloadInvoice - Handler para descargar factura
 * @returns {Array} Datos formateados para la tabla
 */
export const transformPurchasesForTable = (purchases, onViewDetails, onDownloadInvoice) => {
  if (!purchases || !Array.isArray(purchases)) {
    return [];
  }

  return purchases.map(purchase => ({
    ...purchase,
    onViewDetails,
    onDownloadInvoice,
  }));
};

export default {
  getPurchaseTableColumns,
  transformPurchasesForTable,
};
